import { Link } from "react-router-dom";
import {
  Sparkle, ArrowRight, Handshake, MagnifyingGlass, Buildings, Stack,
  Code, Briefcase, Bank, Globe, Lightning,
} from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

/**
 * Orixa Labs — preview of upcoming modules (Phase 5 skeleton).
 *
 * Each card shows what's coming next and lets the user register interest.
 */
const FUTURE_MODULES = [
  {
    id: "capital_match",
    icon: Handshake,
    accent: "teal",
    title: "Orixa Capital Match",
    eta: "Q2 2026",
    tagline: "AI-matched investor introductions — anchor LPs, DFIs, family offices and strategic capital.",
    why: "Reduce time-to-first-meeting from weeks to days with curated, warm-introduced shortlists.",
  },
  {
    id: "due_diligence",
    icon: MagnifyingGlass,
    accent: "navy",
    title: "Orixa Due Diligence",
    eta: "Q2 2026",
    tagline: "8-stream institutional DD with automated red-flag detection and exposure scoring.",
    why: "Compress a 12-week DD into 3-4 weeks while raising rigor.",
  },
  {
    id: "deal_room",
    icon: Stack,
    accent: "gold",
    title: "Orixa Deal Room",
    eta: "Q3 2026",
    tagline: "Secure virtual data room with watermarked access, audit trail and AI-driven Q&A summaries.",
    why: "Replace Intralinks/Datasite for mid-market emerging-markets deals.",
  },
  {
    id: "family_office_suite",
    icon: Buildings,
    accent: "navy",
    title: "Family Office Suite",
    eta: "Q3 2026",
    tagline: "Multi-asset consolidation, succession planning and governance for principal families.",
    why: "Purpose-built for African and GCC family offices currently using fragmented tools.",
  },
  {
    id: "institutional_api",
    icon: Code,
    accent: "teal",
    title: "Orixa Institutional API",
    eta: "Q3 2026",
    tagline: "Programmatic access to workflows, deliverables and Radar — embed Orixa into your stack.",
    why: "Banks and DFIs can pipe Orixa into their existing investment committee tooling.",
  },
  {
    id: "trade_finance",
    icon: Briefcase,
    accent: "gold",
    title: "Orixa Trade Finance",
    eta: "Q4 2026",
    tagline: "LC/SBLC structuring, exporter risk profiling and trade corridor intelligence.",
    why: "Plug Orixa into intra-African and EM cross-border trade flows.",
  },
  {
    id: "corporate_banking_gateway",
    icon: Bank,
    accent: "navy",
    title: "Corporate Banking Gateway",
    eta: "Q4 2026",
    tagline: "Direct connectivity into Ibn Miyeta Group partner banks for execution.",
    why: "Bridge Orixa's intelligence with on-rails corporate banking action.",
  },
];

const ACCENT_MAP = {
  teal: "bg-teal-soft text-teal",
  gold: "bg-gold-soft text-gold",
  navy: "bg-secondary text-primary",
};

export default function Labs() {
  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="labs-page">
      <PageHeader
        eyebrow="What's next"
        title="Orixa Labs"
        subtitle="Orixa is being built as a long-term agentic intelligence platform. Here's what's coming."
      />

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="labs-grid">
        {FUTURE_MODULES.map((m) => {
          const Icon = m.icon;
          return (
            <div
              key={m.id}
              className="bg-card border border-border rounded-xl p-6 flex flex-col"
              data-testid={`labs-card-${m.id}`}
            >
              <div className="flex items-start justify-between mb-5 gap-3">
                <div className={`h-11 w-11 rounded-lg grid place-items-center flex-shrink-0 ${ACCENT_MAP[m.accent] || ACCENT_MAP.navy}`}>
                  <Icon size={20} weight="duotone" />
                </div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-gold bg-gold-soft px-2 py-0.5 rounded-full">
                  {m.eta}
                </div>
              </div>
              <div className="font-display text-lg sm:text-xl tracking-tight leading-tight mb-2">{m.title}</div>
              <div className="text-sm text-muted-foreground mb-4 leading-relaxed">{m.tagline}</div>
              <div className="mt-auto pt-4 border-t border-border">
                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Why it matters</div>
                <div className="text-xs text-muted-foreground leading-snug">{m.why}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Early access CTA */}
      <div className="mt-12 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8 card-elev" data-testid="labs-early-access-cta">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 bg-white/10 rounded-lg grid place-items-center flex-shrink-0">
            <Lightning size={22} weight="duotone" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80 mb-1">Early access</div>
            <div className="font-display text-xl sm:text-2xl tracking-tight leading-tight">Want a private preview?</div>
            <div className="text-sm opacity-90 mt-2 leading-relaxed max-w-2xl">
              Family offices, DFIs and institutional partners can request a guided walkthrough of upcoming modules before public release.
            </div>
          </div>
          <a
            href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest"
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-primary rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-white/95"
            data-testid="labs-request-access"
          >
            Book a product demo <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </div>

      <Link to="/dashboard" className="mt-10 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        ← Back to dashboard
      </Link>
    </div>
  );
}
